/** The accounts module's API types: what its endpoints answer and take. */
import type { LicenseState } from '../add-ons/index.js'
import type { WorkerRole, WorkerTier } from '../core/index.js'

export type SignInUserKind = 'root' | 'admin' | 'sales' | 'operator' | 'dashboard' | 'facility_administrator' | 'worker'

/** One row of `GET /admin/sign-ins`: a person with a sign-in on record. `userKey` is what the events call takes. */
export interface SignInUser {
  userKey: string
  userKind: SignInUserKind
  userId: string
  clientId: string | null
  organizationName: string | null
  name: string | null
  login: string
  count: number
  firstAt: string
  lastAt: string
}

/** One successful sign-in, from `GET /admin/sign-ins/events`. */
export interface SignInEvent {
  eventId: string
  userKind: SignInUserKind
  userId: string
  clientId: string | null
  organizationName: string | null
  name: string | null
  login: string
  at: string
  userAgent: string | null
}

export type DemoSessionRole = 'operator' | 'facility_administrator' | 'worker'

/** Body of `POST .../demo-sessions`: which kind of person, and which one (operatorId, administratorId or workerId). */
export interface OpenDemoSessionInput {
  as: DemoSessionRole
  subject: string
}

/** One person a demo client can be opened as; `subject` is what the open call takes. */
export interface DemoPerson {
  role: DemoSessionRole
  subject: string
  label: string
  /** A worker's tier, so a demo can pick someone by it; absent for staff and from an API that does not send it yet. */
  tier?: WorkerTier
}

export interface DemoPeopleFacility {
  facilityId: string
  name: string
  /** Null where the API does not know it cheaply. */
  workerCount: number | null
}

/** Who the caller could open, grouped as the chooser shows them. */
export interface DemoPeople {
  groups: { label: string; people: DemoPerson[] }[]
  /** When the workers were left out of `groups`: the facilities whose workers can be opened, in name order, to be asked for one at a time. */
  facilities?: DemoPeopleFacility[]
  /** For a demo session only: the account that opened it, offered as the way back. */
  opener?: DemoOpener
}

export interface DemoOpener {
  kind: 'root' | 'admin' | 'operator' | 'facility_administrator'
  label: string
}

/** The record of one demo session: who opened a demo client as whom, and when. */
export interface DemoSession {
  sessionId: string
  clientId: string
  as: DemoSessionRole
  subject: string
  subjectName: string | null
  openedBy: string
  openedVia: 'admin' | 'operator' | 'facility_administrator'
  openedAt: string
  expiresAt: string
}

/** What `POST .../demo-sessions` answers: a token as the person's own login would give it, plus who it is. */
export interface DemoSessionResponse {
  accessToken: string
  tokenType: 'Bearer'
  expiresIn: number
  role: DemoSessionRole
  username: string
  name: string | null
  session: DemoSession
}

export interface Me {
  clientId: string
  organizationName: string
  /** True for a demo client. Absent from an API that predates demo sessions. */
  isDemo?: boolean
  /** Set when this session was opened without a password as one of a demo client's people: who opened it and when it ends. */
  demoSession?: { openedBy: string; expiresAt: string | null } | null
  /**
   * Whether this session may open or switch a demo session (View as…, Switch): always for a session a
   * SeeWhoCares admin opened, for the client's own staff only where the admin switched it on. Absent
   * from an API that predates the switch.
   */
  viewAs?: boolean
  // Null when the signed-in identity predates profile support and has no
  // name on file yet - the header falls back to the raw username/email.
  firstName: string | null
  lastName: string | null
  /** Present for a worker's own login only: which roster record the token names. */
  worker?: MeWorker
  /** The client's add-on licenses by key (docs/plans/feature-licensing-plan.md). Absent from an API that predates add-ons. */
  features?: Record<string, LicenseState>
  /** Whether the deployment writes AI briefings and can email one (docs/plans/ai-briefings-plan.md). Absent from an API without the add-on. */
  ai?: { configured: boolean; emailConfigured: boolean }
}

export interface MeWorker {
  workerId: string
  facilityId: string
  facilityName: string | null
  role: WorkerRole
  /** Which add-ons are on at the worker's own facility. Absent from an API that predates add-ons. */
  features?: Record<string, boolean>
}

/** A worker's own sign-in, as `GET .../workers/{workerId}/login` returns it: never the hash. */
export interface WorkerLogin {
  clientId: string
  workerId: string
  email: string
  createdAt: string
  lastSignedInAt: string | null
  /** `pending` while the worker's invitation waits (invitations plan D2); absent on a login made with a password. */
  invitationStatus?: 'pending' | 'accepted'
  /** When the worker accepted their invitation; absent before 29 October 2026 and for a login made with a password. */
  invitationAcceptedAt?: string
}

/** Body of `POST .../workers/{workerId}/login/invitation` (invitations plan phase 3): the email, and a text as well to the known mobile (`text`) or one typed here (`mobile`). */
export interface InviteWorkerInput {
  email: string
  text?: boolean
  mobile?: string
}

/** What `GET .../workers/{workerId}/login/invitation` answers: the open invitation, whether the stack emails, and the masked mobile a text could go to. */
export interface WorkerInvitationState {
  invitation: Invitation | null
  canEmail: boolean
  mobile: string | null
}

/** Body of `POST .../workers/{workerId}/login`. The password is generated by the API and returned once. */
export interface CreateWorkerLoginInput {
  email: string
}

/** Body of `PATCH .../workers/{workerId}/login`: a new email, a new password, or both. */
export interface UpdateWorkerLoginInput {
  email?: string
  rotatePassword?: boolean
}

export interface ResetLink {
  resetUrl: string
  expiresInSeconds: number
}

export interface FacilityAdministrator {
  administratorId: string
  clientId: string
  email: string
  facilityIds: string[]
  createdAt: string
  firstName: string
  lastName: string
  /** Their phone as written, shown on their facilities' dashboards; null or absent means none recorded. */
  phone?: string | null
  /** `pending` while the administrator's invitation waits (invitations plan D2); absent on a login from before, which reads as accepted. */
  invitationStatus?: 'pending' | 'accepted'
}

/** Body of `POST /admin/clients/{clientId}/operators/invitations`. */
export interface InviteOperatorInput {
  email: string
  firstName: string
  lastName: string
}

/** Body of `POST /admin/clients/{clientId}/facility-administrators/invitations`. */
export interface InviteFacilityAdministratorInput extends InviteOperatorInput {
  facilityIds: string[]
  /** Optional; kept as written. */
  phone?: string
}

export interface CreateFacilityAdministratorInput {
  email: string
  facilityIds: string[]
  firstName: string
  lastName: string
  /** Optional; kept as written. */
  phone?: string
}

export interface FacilityAdministratorWithPassword extends FacilityAdministrator {
  password: string
}

export interface UpdateFacilityAdministratorInput {
  facilityIds?: string[]
  rotatePassword?: boolean
  email?: string
  firstName?: string
  lastName?: string
  /** Null clears it. */
  phone?: string | null
}

export interface UpdateFacilityAdministratorResult extends FacilityAdministrator {
  password?: string
}

import type { AdminRole } from '../core/index.js'

/** `POST /admin/login`: who signed in to the admin area, and how much feedback arrived since their previous sign-in. */
export interface AdminLoginResult {
  username: string
  role: AdminRole
  firstName: string
  lastName: string
  loggedInAt: string
  previousLoginAt: string | null
  newFeedback: number
}

/** One row of `GET /admin/admins`; never carries a hash. */
export interface AdminUser {
  username: string
  role: AdminRole
  firstName: string
  lastName: string
  createdAt: string
  createdBy: string
  lastLoginAt: string | null
  /** Where the admin is reached, set by the root; the root's is where new feedback is emailed. Never the login. */
  email: string | null
  /** `pending` while the admin's invitation waits (invitations plan D2); absent on a login from before, which reads as accepted. */
  invitationStatus?: 'pending' | 'accepted'
}

/** An invitation as the inviter's lists show it (`GET /admin/admins/invitations`, `GET /admin/invitations`): never the token or the code. */
export interface Invitation {
  invitationId: string
  kind: 'admin' | 'operator' | 'facility_administrator' | 'worker'
  subjectId: string
  clientId: string | null
  email: string
  mobile: string | null
  firstName: string
  lastName: string
  role: 'admin' | 'sales' | null
  about: { organizationName: string | null; facilityNames: string[] }
  invitedBy: { sub: string; audienceType: string; name: string | null }
  createdAt: string
  expiresAt: string
  status: 'pending' | 'accepted' | 'revoked' | 'expired'
  acceptedAt: string | null
  sends: { channel: 'email' | 'sms' | 'link'; at: string; status: 'sent' | 'failed' | 'shown'; reason: string | null }[]
  resentAt: string | null
}

/** What an invite or a resend answers: the invitation, and the link and code only where the deployment could not send them (D6). */
export interface InvitationResult {
  invitation: Invitation
  handOff: { link: string; code: string } | null
}

/** Body of `PATCH /admin/admins/{username}`: the name (both parts together), the contact email (null clears it), or both. */
export interface UpdateAdminInput {
  firstName?: string
  lastName?: string
  email?: string | null
}

/** A created or rotated admin login, with the one time its password is readable. */
export interface AdminUserWithPassword {
  admin: AdminUser
  password: string
}

export interface CreateAdminInput {
  username: string
  firstName: string
  lastName: string
  /** An admin when left out. Set when the login is made and not changed afterwards. */
  role?: 'admin' | 'sales'
}

/** What an invitation link opens onto (`GET /auth/invitations?token=`): who invited, to what, the email it went to, described. */
export interface InvitationPreview {
  kind: 'admin' | 'operator' | 'facility_administrator' | 'worker'
  organizationName: string | null
  facilityNames: string[]
  invitedByName: string | null
  email: string
  firstName: string
  lastName: string
  expiresAt: string
  open: boolean
  status: 'pending' | 'accepted' | 'revoked' | 'expired'
}

/** Body of `POST /auth/invitations/accept`: the link's token, or the email and the code, the password, and the name the person may correct. */
export interface AcceptInvitationInput {
  token?: string
  email?: string
  code?: string
  password: string
  firstName?: string
  lastName?: string
}

/** What an acceptance answers: a session for the operator or worker shell, or the admin's username to sign in with. */
export type AcceptedSession =
  | { kind: 'operator' | 'facility_administrator' | 'worker'; username: string; accessToken: string; tokenType: 'Bearer'; expiresIn: number }
  | { kind: 'admin'; username: string; role: 'admin' | 'sales' }

// --- A staff member's own notification settings (facility operations dashboard plan §5): `/auth/me/notifications`.

export type StaffAlertKind = 'urgent' | 'approvals' | 'digest' | 'beforeRelease' | 'weekly'

export interface StaffNotificationSettingsInput {
  kinds: Record<StaffAlertKind, { sms: boolean; email: boolean }>
  digestAt: string
  quietHours: { start: string; end: string } | null
  facilityIds: string[] | null
  /** "Always email my briefing" (AI briefings plan D13): each AI briefing emailed as soon as it is ready. */
  briefingEmail?: boolean
}

export interface StaffNotificationSettings extends StaffNotificationSettingsInput {
  briefingEmail: boolean
  email: string | null
  mobile: { masked: string; verified: boolean; optedOut: boolean; consentAt: string } | null
  isDefault: boolean
}

/** What a staff member agrees to when they add their mobile, as registered for texts (the API's `SMS_STAFF_ALERT_CONSENT_WORDING`). */
export const STAFF_ALERT_CONSENT_WORDING = 'I agree to receive staffing alerts from autonoME for the facilities I manage by text: what needs me, a daily digest and release reminders, as I choose. Message and data rates may apply. Reply STOP to end, HELP for help.'
